/**
 * A Spectrum-derived palette: the same eight hues the ZX had, plus extra value
 * steps of each so the mountain can have form. Nothing here is off-hue.
 */

export const rgb = (r: number, g: number, b: number): number => (255 << 24) | (b << 16) | (g << 8) | r;
export const css = (u32: number): string =>
  `#${(u32 & 0xff).toString(16).padStart(2, '0')}${((u32 >> 8) & 0xff).toString(16).padStart(2, '0')}${((u32 >> 16) & 0xff).toString(16).padStart(2, '0')}`;

export function blend(a: number, b: number, t: number): number {
  const ar = a & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = (a >> 16) & 0xff;
  const br = b & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = (b >> 16) & 0xff;
  return rgb(
    (ar + (br - ar) * t) | 0,
    (ag + (bg - ag) * t) | 0,
    (ab + (bb - ab) * t) | 0,
  );
}

// Canonical Spectrum inks -----------------------------------------------------
export const BLACK = rgb(0x00, 0x00, 0x00);
export const BLUE = rgb(0x00, 0x00, 0xd7);
export const BLUE_B = rgb(0x00, 0x00, 0xff);
export const RED = rgb(0xd7, 0x00, 0x00);
export const RED_B = rgb(0xff, 0x00, 0x00);
export const MAGENTA = rgb(0xd7, 0x00, 0xd7);
export const MAGENTA_B = rgb(0xff, 0x00, 0xff);
export const GREEN = rgb(0x00, 0xd7, 0x00);
export const GREEN_B = rgb(0x00, 0xff, 0x00);
export const CYAN = rgb(0x00, 0xd7, 0xd7);
export const CYAN_B = rgb(0x00, 0xff, 0xff);
export const YELLOW = rgb(0xd7, 0xd7, 0x00);
export const YELLOW_B = rgb(0xff, 0xff, 0x00);
export const WHITE = rgb(0xd7, 0xd7, 0xd7);
export const WHITE_B = rgb(0xff, 0xff, 0xff);

// Extra value steps ----------------------------------------------------------
export const INK = rgb(0x12, 0x12, 0x1c);
export const INK2 = rgb(0x1e, 0x1e, 0x2a);
export const SLATE = rgb(0x35, 0x35, 0x4a);
export const SLATE2 = rgb(0x46, 0x46, 0x5c);
export const SLATE3 = rgb(0x57, 0x57, 0x6e);
export const ORANGE = rgb(0xff, 0x87, 0x00);
export const ORANGE_D = rgb(0xa8, 0x3c, 0x00);

// Terrain shade sets. The renderer dithers between them with the per-cell
// texture byte, which is what gives the ground its grain.
export const SKY_TOP = rgb(0x08, 0x0e, 0x3a);
export const SKY_MID = rgb(0x18, 0x24, 0x8a);
export const SKY_LOW = rgb(0x5f, 0x76, 0xd7);
export const SMOKE_SKY = rgb(0x8a, 0x50, 0x18);

export const ROCK = [rgb(0x2e, 0x2e, 0x40), SLATE, SLATE2, SLATE3];
export const PINE = [rgb(0x00, 0x2a, 0x10), rgb(0x00, 0x3d, 0x14), rgb(0x00, 0x52, 0x1a), rgb(0x00, 0x6b, 0x22)];
export const SCRUB = [rgb(0x33, 0x3d, 0x10), rgb(0x42, 0x4e, 0x14), rgb(0x52, 0x60, 0x1a), rgb(0x62, 0x72, 0x20)];
export const OLIVE = [rgb(0x4a, 0x50, 0x22), rgb(0x5a, 0x60, 0x2a), rgb(0x6b, 0x72, 0x33), rgb(0x7a, 0x82, 0x3c)];
export const OLIVE_LINE = rgb(0x36, 0x3a, 0x16);
export const GRASS = [rgb(0x6b, 0x6b, 0x1e), rgb(0x7a, 0x7a, 0x24), rgb(0x8c, 0x8c, 0x2c), rgb(0x9e, 0x9e, 0x34)];
export const WATER = [rgb(0x00, 0x00, 0x6e), rgb(0x00, 0x00, 0xa8), BLUE, rgb(0x00, 0x5f, 0xd7)];
// Acequias are hand-dug channels a foot wide, not neon strip lighting. At full
// Spectrum cyan they dominated the whole mountain and made the terrain read as
// horizontal stripes; these are visible as water without shouting.
export const ACEQUIA = [rgb(0x0e, 0x5c, 0x66), rgb(0x14, 0x86, 0x92)];
export const ROAD = [rgb(0x4a, 0x4a, 0x58), rgb(0x5e, 0x5e, 0x6e), rgb(0x6e, 0x6e, 0x7e)];
export const BUILDING = [rgb(0x7a, 0x7a, 0x82), rgb(0x8c, 0x8c, 0x96)];
export const PAD = [rgb(0x3a, 0x3a, 0x4e), rgb(0x46, 0x46, 0x5c)];

/** Flame ramp, coolest to hottest. */
export const FIRE = [ORANGE_D, RED, RED_B, ORANGE, YELLOW_B, WHITE_B];
export const BURNT = [rgb(0x14, 0x14, 0x1a), rgb(0x1e, 0x1e, 0x26), rgb(0x28, 0x28, 0x30)];

// CSS strings for sprites, particles and the HUD ------------------------------
export const C = {
  black: css(BLACK),
  ink: css(INK),
  ink2: css(INK2),
  slate: css(SLATE),
  white: css(WHITE),
  whiteB: css(WHITE_B),
  red: css(RED),
  redB: css(RED_B),
  cyan: css(CYAN),
  cyanB: css(CYAN_B),
  yellow: css(YELLOW),
  yellowB: css(YELLOW_B),
  green: css(GREEN),
  greenB: css(GREEN_B),
  magenta: css(MAGENTA),
  magentaB: css(MAGENTA_B),
  blue: css(BLUE),
  blueB: css(BLUE_B),
  orange: css(ORANGE),
} as const;
